/**
 * Phase 9c — pretty audit log details rendering.
 * Plain JS file (.js, no JSX) to dodge the visual-edits babel plugin's
 * JSXOpeningElement recursion bug on this content.
 */
import React from "react";

const fmtMoney = (v) =>
  "$" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyKey = (k) =>
  String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const MONEY_KEY_RE = /amount|total|gross|net|paye|debit|credit|salary|paid/i;

export function summarizeAuditDetails(action, d, actor) {
  const x = d || {};
  const r = (s) => s || "";
  const ref = x.reference_number || x.reference || "";
  const period =
    x.period_year && x.period_month
      ? x.period_year + "-" + String(x.period_month).padStart(2, "0")
      : "";
  const amt = x.amount ? " · " + fmtMoney(x.amount) : "";

  switch (action) {
    case "login": return "Logged in" + (x.ip ? " from " + x.ip : "");
    case "login_failed": return "Failed login for " + (x.email || actor || "(unknown)");
    case "logout": return "Logged out";
    case "user_create": return "Created user " + r(x.email) + " (" + (r(x.role) || "?") + ")";
    case "user_update": {
      const fields = Object.keys(x).filter((k) => k !== "email").map(prettyKey).join(", ");
      return "Updated user " + r(x.email) + (fields ? " — " + fields : "");
    }
    case "user_delete": return "Deleted user " + r(x.email) + " (was " + (r(x.role) || "?") + ")";
    case "password_reset": return "Reset password for " + r(x.email) + (x.method ? " · " + x.method : "");
    case "company_update": return "Updated company profile — " + Object.keys(x).map(prettyKey).join(", ");
    case "transaction_create":
      return "Created " + (r(x.type) || "transaction") + (x.counterparty_name ? " for " + x.counterparty_name : "") + amt;
    case "transaction_submit": return "Submitted transaction " + ref + " to Salihi";
    case "transaction_review": return "Reviewed transaction " + ref;
    case "transaction_verify": return "Verified transaction " + ref;
    case "transaction_post": return "Posted transaction " + ref + amt;
    case "transaction_reject": return "Rejected transaction " + ref + (x.reason ? " — " + x.reason : "");
    case "journal_entry_post":
      return "Posted JE " + r(x.reference) + (x.total_debit ? " — " + fmtMoney(x.total_debit) : "");
    case "payroll_create": return "Created payroll run for " + period;
    case "payroll_approve": return "Approved payroll for " + period;
    case "payroll_post":
      return "Posted payroll for " + period + " — " + (x.employee_count || "?") + " employees, gross " +
        (x.total_gross ? fmtMoney(x.total_gross) : "?");
    case "loan_disbursement": return "Disbursed loan " + r(x.loan_number) + amt;
    case "loan_payment":
      return "Recorded loan payment " + r(x.loan_number) + " installment " + (x.installment_no || "?") + amt;
    case "loan_payoff": return "Paid off loan " + r(x.loan_number) + amt;
    case "tax_record_create":
      return "Recorded " + String(x.tax_type || "").toUpperCase() + " payment for " + r(x.period) +
        " — actual " + (x.actual ? fmtMoney(x.actual) : "?") +
        (x.estimated ? " (est. " + fmtMoney(x.estimated) + ")" : "");
    case "tax_record_update": return "Updated tax record — " + Object.keys(x).map(prettyKey).join(", ");
    case "tax_record_delete": return "Deleted tax record (" + (r(x.tax_type) || "?") + " · " + (r(x.period) || "?") + ")";
    case "compliance_create": return "Created compliance item: " + r(x.title);
    case "compliance_update": return "Updated compliance item" + (x.title ? ": " + x.title : "");
    case "compliance_complete": return "Marked complete" + (x.title ? ": " + x.title : "");
    case "report_export":
      return "Exported " + (r(x.report_id || x.report) || "report") + " as " + String(x.format || "").toUpperCase();
    case "admin_reset":
      return "Reset all system data — " + (x.collections_truncated != null ? x.collections_truncated :
        x.wiped_count != null ? x.wiped_count : "?") + " collections truncated";
    case "admin_override_edit": {
      const fields = (x.fields_changed || []).map(prettyKey).join(", ");
      const reversed = x.je_reversed_id ? " · JE reversed & re-posted" : "";
      return "Admin override edit on " + (x.prior_status || "transaction") +
        (fields ? " — changed " + fields : "") + reversed;
    }
    case "admin_override_delete": {
      const soft = x.soft ? "soft" : "hard";
      const prior = x.prior_status || "?";
      const rev = x.reversing_je_id ? " · reversing JE posted" : "";
      const b = x.before || {};
      const tail = b.amount ? " — " + fmtMoney(b.amount) : "";
      return "Admin " + soft + "-deleted " + prior + tail + rev;
    }
    default: {
      const bits = Object.entries(x).slice(0, 4).map(function (pair) {
        const k = pair[0]; const v = pair[1];
        return prettyKey(k) + ": " + (typeof v === "object" ? "…" : String(v).slice(0, 40));
      });
      return bits.length ? bits.join(" · ") : "(no details)";
    }
  }
}

function renderValue(k, v) {
  if (v === null || v === undefined)
    return React.createElement("span", { className: "text-slate-400" }, "—");
  if (Array.isArray(v)) {
    if (v.length === 0)
      return React.createElement("span", { className: "text-slate-400" }, "empty");
    return React.createElement("span", { className: "text-slate-600" }, v.length + " items");
  }
  if (typeof v === "object") {
    const keys = Object.keys(v);
    if (keys.length === 0)
      return React.createElement("span", { className: "text-slate-400" }, "(empty)");
    const preview = keys.slice(0, 3).map(prettyKey).join(", ") + (keys.length > 3 ? "…" : "");
    return React.createElement("span", { className: "text-slate-600" }, "{" + preview + "}");
  }
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number" && Math.abs(v) >= 1 && MONEY_KEY_RE.test(String(k))) {
    return fmtMoney(v);
  }
  return String(v);
}

export function KvTable(props) {
  const details = props.details;
  if (!details || typeof details !== "object") {
    return React.createElement("span", { className: "text-xs text-slate-600" }, String(details == null ? "—" : details));
  }
  const flat = Array.isArray(details)
    ? details.map((v, i) => [String(i), v])
    : Object.entries(details);
  if (flat.length === 0)
    return React.createElement("span", { className: "text-xs text-slate-400" }, "(empty)");
  return React.createElement(
    "div",
    { className: "rounded border border-slate-200 bg-white overflow-hidden" },
    React.createElement(
      "table",
      { className: "w-full text-xs" },
      React.createElement(
        "tbody",
        null,
        flat.map((pair) => {
          const k = pair[0]; const v = pair[1];
          return React.createElement(
            "tr",
            { key: k, className: "border-b border-slate-100 last:border-0" },
            React.createElement(
              "td",
              { className: "bg-slate-50 px-3 py-1.5 font-semibold text-slate-700 align-top whitespace-nowrap w-1/3" },
              prettyKey(k),
            ),
            React.createElement(
              "td",
              { className: "px-3 py-1.5 text-slate-700 break-words" },
              renderValue(k, v),
            ),
          );
        }),
      ),
    ),
  );
}
